import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { showDatabricksProvisionWarnings } from '@/lib/databricksProvisionWarnings'
import { toast } from 'sonner'
import { Add24Regular, FolderList20Regular, FolderOpen24Regular, Notebook24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { useCurrentProject } from '../useCurrentProject'
import { getProjectOrganisationId } from '../useUserLinkedProjects'

const statusIcon = (status?: string) => {
  if (status === 'Check') return <span className="text-green-500">✔</span>
  if (status === 'Warn') return <span className="text-yellow-500">⚠</span>
  if (status === 'Problem') return <span className="text-red-500">✖</span>
  return <span className="text-light">{status}</span>
}

export function ProjectDatabricksNotebooksPageContent() {
  const project = useCurrentProject()
  const { projectId } = project
  const organisationId = getProjectOrganisationId(project)
  const [busy, setBusy] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteResponse, setDeleteResponse] = useState<string[] | null>(null)

  const {
    getDatabricksApiGetDatabricksProjectStatus,
    getDatabricksApiGetDatabricksProjectIntegrationStatus,
    putDatabricksApiProvisionDatabricksProject,
    postDatabricksApiPromoteDatabricksProject,
    postDatabricksApiPromoteProjectNotebook,
    putDatabricksApiAddDefaultProjectNotebook,
    putDatabricksApiCreateDatabricksProjectIntegration,
    postDatabricksApiDeleteDatabricksProjectIntegration,
  } = useRestClient()

  const invalidateProject = () => getDatabricksApiGetDatabricksProjectStatus.invalidateQueries()
  const invalidateIntegration = () => getDatabricksApiGetDatabricksProjectIntegrationStatus.invalidateQueries()

  const { data: projectStatus, isLoading } = getDatabricksApiGetDatabricksProjectStatus.useQuery({
    query: { projectId },
  })

  const { mutate: provisionProject } = putDatabricksApiProvisionDatabricksProject.useMutation()
  const { mutate: promoteProject } = postDatabricksApiPromoteDatabricksProject.useMutation()
  const { mutate: promoteNotebook } = postDatabricksApiPromoteProjectNotebook.useMutation()
  const { mutate: addDefaultNotebook } = putDatabricksApiAddDefaultProjectNotebook.useMutation()
  const { mutate: provisionIntegration, isPending: isProvisioningIntegration } =
    putDatabricksApiCreateDatabricksProjectIntegration.useMutation()
  const { mutate: deleteIntegration, isPending: isDeletingIntegration } =
    postDatabricksApiDeleteDatabricksProjectIntegration.useMutation()

  if (isLoading || !projectStatus) {
    return <div className="p-6 text-sm text-muted-foreground">Loading project Databricks status...</div>
  }

  const onProvision = () => {
    setBusy(true)
    toast.info('Provisioning project on Databricks...')
    provisionProject(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error provisioning project: ' + getDatabricksErrorMessage(e)),
        onSuccess: (data) => {
          toast.success('Project provisioned')
          if (data?.warnings?.length) showDatabricksProvisionWarnings(data)
        },
        onSettled: () => {
          setBusy(false)
          invalidateProject()
          invalidateIntegration()
        },
      }
    )
  }

  const onPromote = () => {
    setBusy(true)
    toast.info('Promoting project to production...')
    promoteProject(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error promoting project: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => toast.success('Project promoted to production'),
        onSettled: () => {
          setBusy(false)
          invalidateProject()
        },
      }
    )
  }

  const onProvisionIntegration = () => {
    toast.info('Provisioning project integration...')
    provisionIntegration(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error provisioning integration: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => {
          toast.success('Provisioned project integration')
          invalidateIntegration()
        },
      }
    )
  }

  const onDeleteIntegration = () => {
    if (!organisationId) {
      toast.error('Organisation ID not found')
      return
    }
    deleteIntegration(
      {
        query: {
          organisationId: String(organisationId),
          projectId: String(projectId),
        },
      },
      {
        onSuccess: (data) => {
          setDeleteResponse(data ?? [])
          setDeleteConfirmOpen(false)
          invalidateIntegration()
        },
        onError: (e) => {
          toast.error('Error deleting integration: ' + getDatabricksErrorMessage(e))
          setDeleteConfirmOpen(false)
        },
      }
    )
  }

  const onPromoteNotebook = (notebookPath: string) => {
    setBusy(true)
    promoteNotebook(
      { query: { projectId, notebookPath } },
      {
        onError: (e) => toast.error('Error promoting notebook: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => toast.success('Notebook promoted'),
        onSettled: () => {
          setBusy(false)
          invalidateProject()
        },
      }
    )
  }

  const onAddNotebook = () => {
    setBusy(true)
    addDefaultNotebook(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error adding notebook: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => toast.success('Notebook added'),
        onSettled: () => {
          setBusy(false)
          invalidateProject()
        },
      }
    )
  }

  return (
    <div className="pt-5">
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open)
          if (!open) setDeleteResponse(null)
        }}
        title="Delete project integration"
        description="Are you sure you want to delete this Databricks project integration and all associated resources?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={onDeleteIntegration}
        isSubmitting={isDeletingIntegration}
      />
      <Dialog open={deleteResponse !== null} onOpenChange={(open) => !open && setDeleteResponse(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete integration result</DialogTitle>
            <DialogDescription asChild>
              <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-sm">
                {deleteResponse?.length ? deleteResponse.join('\n\n') : 'No feedback items.'}
              </pre>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDeleteResponse(null)}>Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-y-5 py-4">
        <div className="rounded bg-background p-6">
          <h6 className="mb-4 border-b pb-2">Development checks</h6>
          <div className="flex flex-wrap gap-x-20 gap-y-4">
            {projectStatus.developmentItems?.map(({ name, icon, messages }) => (
              <div key={name} className="flex flex-col">
                <span className="text-sm font-light">{name}:</span>
                <span>{statusIcon(icon)} <span className="font-light">{messages}</span></span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded bg-background p-6">
          <h6 className="mb-4 border-b pb-2">Production checks</h6>
          <div className="flex flex-wrap gap-x-20 gap-y-4">
            {projectStatus.productionItems?.map(({ name, icon, messages }) => (
              <div key={name} className="flex flex-col">
                <span className="text-sm font-light">{name}:</span>
                <span>{statusIcon(icon)} <span className="font-light">{messages}</span></span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded bg-background p-6">
          <div className="flex flex-wrap gap-3">
            <Button disabled={busy} onClick={onProvision}>Provision project</Button>
            <Button disabled={busy} variant="outline" onClick={onPromote}>Promote project</Button>
            <Button
              disabled={isProvisioningIntegration}
              variant="outline"
              onClick={onProvisionIntegration}
            >
              Provision integration
            </Button>
            <Button
              disabled={isDeletingIntegration}
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete integration
            </Button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!projectStatus.devFolderUrl} onClick={() => window.open(projectStatus.devFolderUrl, '_blank')}>
                <FolderOpen24Regular /> Development
              </Button>
              <Button size="sm" variant="outline" disabled={!projectStatus.prodFolderUrl} onClick={() => window.open(projectStatus.prodFolderUrl, '_blank')}>
                <FolderOpen24Regular /> Production
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-b pb-1">
        <h5>Notebooks</h5>
        <Button disabled={busy} variant="ghost" onClick={onAddNotebook}>
          <Add24Regular />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
        {projectStatus.notebooks?.map(({ notebookPath, language, notebookUrl }) => (
          <div key={notebookPath} className="grid grid-cols-[1fr_4fr] gap-2 rounded-lg bg-background p-4 shadow">
            <div className="flex size-12 items-center justify-center rounded-full bg-muted">
              <Notebook24Regular />
            </div>
            <div>
              <div>{notebookPath}</div>
              <div className="text-xs font-light">{language}</div>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => onPromoteNotebook(notebookPath!)}>Promote</Button>
                <Button size="sm" onClick={() => window.open(notebookUrl, '_blank')}>Edit</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 border-b pb-1">
        <h5>Production Notebooks</h5>
      </div>
      <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
        {projectStatus.productionNotebooks?.length === 0 ? (
          <p className="text-sm text-muted-foreground">No production notebooks yet.</p>
        ) : (
          projectStatus.productionNotebooks?.map(({ notebookPath, language, notebookUrl }) => (
            <div key={notebookPath} className="grid grid-cols-[1fr_4fr] gap-2 rounded-lg bg-background p-4 shadow">
              <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                <FolderList20Regular />
              </div>
              <div>
                <div>{notebookPath}</div>
                <div className="text-xs font-light">{language}</div>
                <Button size="sm" className="mt-2" onClick={() => window.open(notebookUrl, '_blank')}>View</Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
